import React from 'react';
import PageHero from '@/components/site/PageHero';
import SectionHeader from '@/components/site/SectionHeader';
import ClosingCta from '@/components/site/ClosingCta';
import PrinciplesList from '@/components/site/PrinciplesList';
import GoldText from '@/components/site/GoldText';
import { founder, relevantWork } from '@/content/business';
import { usePageMeta } from '@/hooks/usePageMeta';

const howWeWork = [
  ['Communication', 'One named point of contact on each side, with a regular check-in during the pilot.'],
  [
    'Decision ownership',
    'You own decisions about your business, data, and customers. We own the implementation we agreed to deliver.',
  ],
  ['Approvals', 'Scope, sources, permissions, and launch are approved by you in writing before they take effect.'],
  ['Handoff', 'Documentation, access, and ongoing responsibilities are confirmed before the engagement closes.'],
];

const About = () => {
  usePageMeta(
    'About',
    'GoldCrusade helps businesses move from an AI idea to a defined workflow, a working pilot, and an informed next decision.',
  );

  return (
    <>
      <PageHero
        eyebrow="About GoldCrusade"
        title={
          <>
            Practical AI.
            <br />
            <GoldText>Accountable implementation.</GoldText>
          </>
        }
      >
        <p>
          GoldCrusade helps businesses move from an AI idea to a defined workflow, a working pilot, and an informed next
          decision.
        </p>
      </PageHero>

      <section aria-labelledby="why-title" className="border-t border-rule">
        <div className="section-container grid gap-12 lg:grid-cols-2">
          <SectionHeader id="why-title" title="Why GoldCrusade">
            <p>
              Most businesses don’t need an AI strategy deck. They need one piece of repetitive work handled better, and
              an honest read on whether it worked.
            </p>
            <p>
              So we focus on practical assistants built around real workflows, and on the ongoing guidance that helps a
              business decide what to do next—based on evidence from its own operations.
            </p>
          </SectionHeader>
          <PrinciplesList />
        </div>
      </section>

      <section aria-labelledby="lead-title" className="border-t border-rule">
        <div className="section-container">
          {founder ? (
            <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
              <img
                src={founder.photo}
                alt={founder.name}
                className="aspect-[4/5] w-full border border-rule2 object-cover"
              />
              <div>
                <SectionHeader id="lead-title" eyebrow="Who leads the work" title={founder.name}>
                  <p className="text-softwhite">{founder.role}</p>
                  {founder.bio.map((para) => (
                    <p key={para}>{para}</p>
                  ))}
                </SectionHeader>
                {founder.experience.length > 0 && (
                  <ul className="mt-6 list-disc space-y-2 pl-5 text-mist">
                    {founder.experience.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <SectionHeader
              id="lead-title"
              eyebrow="Who leads the work"
              title="The person you meet is the person who builds it."
            >
              <p>
                Discovery, scoping, implementation, and the results review are led by GoldCrusade’s founder—not handed
                off to a team you haven’t met.
              </p>
            </SectionHeader>
          )}
        </div>
      </section>

      <section aria-labelledby="engage-title" className="border-t border-rule">
        <div className="section-container">
          <SectionHeader id="engage-title" title="How engagements operate" />
          <dl className="mt-10 grid gap-4 sm:grid-cols-2">
            {howWeWork.map(([term, desc]) => (
              <div key={term} className="surface p-6">
                <dt className="mb-2 font-serif text-2xl">{term}</dt>
                <dd className="text-mist">{desc}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {relevantWork.length > 0 && (
        <section aria-labelledby="work-title" className="border-t border-rule">
          <div className="section-container">
            <SectionHeader id="work-title" title="Relevant work">
              <p>
                Each example is labeled by type. Earlier website and automation work is not presented as AI pilot
                results.
              </p>
            </SectionHeader>
            <ul className="mt-10 grid gap-4 md:grid-cols-3">
              {relevantWork.map((item) => (
                <li key={item.title} className="surface p-6">
                  <p className="eyebrow mb-3">{item.kind}</p>
                  <h3 className="mb-2 text-2xl">{item.title}</h3>
                  <p className="text-sm text-mist">{item.summary}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <ClosingCta
        compact
        title={
          <>
            The goal is not more AI. <GoldText>It is a better-defined way to get useful work done.</GoldText>
          </>
        }
      />
    </>
  );
};

export default About;
